<template>
  <form
    class="vehicle-finder-form__form form-group"
    @submit.prevent="handleSubmit"
  >
    <div class="vehicle-finder-form__types form-group__field">
      <vehicle-type
        form="dropdowns"
        :namespace="localNamespace"
        :parent-type="type"
        :tooltip-active="typeTooltipActive"
        @update-other-selected-type="(type) => handleTypeUpdate('', type)"
        @update-selected-type="handleTypeUpdate"
        @update-tooltip="handleTooltipUpdate"
      />
    </div>

    <div
      ref="dropdownsContainer"
      class="vehicle-finder-form__dropdowns-container"
      :style="dropdownsContainerStyle"
    >
      <div
        ref="dropdowns"
        class="vehicle-finder-form__dropdowns"
        :class="`vehicle-finder-form__dropdowns--${Object.keys(dropdowns).length}`"
      >
        <div
          v-for="(key, index) of Object.keys(dropdowns)"
          :key="`${localNamespace}-${key}-${index}`"
          class="vehicle-finder-form__input form-group__field"
        >
          <label
            class="form-group__label text-body-xs-bold"
            :for="`${localNamespace}-${key}`"
          >
            <span v-text="dropdownTitles[key]" />

            <span
              v-if="optionalTypes.includes(key)"
              class="form-group__optional text-body-xs-regular"
              v-text="$string('general.optional')"
            />

            <abbr
              v-else
              :title="$string('general.required.label')"
              v-text="$string('general.required.symbol')"
            />
          </label>

          <div class="vehicle-finder-form__dropdown-container">
            <transition name="fade">
              <icon-loading
                v-if="getDropdownLoading(key)"
                class="vehicle-finder-form__dropdown-loading"
              />
            </transition>

            <select
              :id="`${localNamespace}-${key}`"
              v-model="selected[key]"
              :class="{ [$class('error')]: error.input === `${localNamespace}-${key}` }"
              autocomplete="off"
              :disabled="getDropdownDisabled(index, key)"
              :required="!optionalTypes.includes(key)"
              :aria-describedby="error.input === `${localNamespace}-${key}`
                ? `${localNamespace}-error`
                : null
              "
              :aria-invalid="error.input === `${localNamespace}-${key}`"
              @change="handleSelectedUpdate(selected[key], key)"
            >
              <option
                hidden
                selected
                value=""
                v-text="dropdownTitles[key]"
              />

              <option
                v-if="optionalTypes.includes(key) && dropdowns[key]?.length && selected[key]"
                value=""
              />

              <option
                v-if="!dropdowns[key]?.length"
                value=""
                v-text="$string('vehicle_finder.dropdowns.empty')"
              />

              <option
                v-for="(item) of dropdowns[key]"
                :key="item[0]?.key"
                :value="item.productMetafield"
                v-text="item.label"
              />
            </select>
          </div>
        </div>
      </div>
    </div>

    <p
      v-show="error.message"
      :id="`${localNamespace}-error`"
      class="vehicle-finder-form__error text-body-s-regular"
      role="status"
      v-text="error.message"
    />

    <div class="vehicle-finder-form__submit form-group__footer">
      <btn
        :label="$string('vehicle_finder.cta_labels.search')"
        :show-state-icon="showSubmitStateIcon"
        type="submit"
      >
        <template #stateIcon>
          <component :is="submitStateIcon" />
        </template>
      </btn>
    </div>
  </form>
</template>

<script>

/**
 * Vue: Dropdowns form (vehicle-finder-form)
 * -----------------------------------------------------------------------------
 * Form to run dropdowns search.
 * - To add a new dropdown:
 *   - Add to selected object.
 *   - Add new locale string to 'vehicle_finder.detail_titles'.
 *   - Add locale string to theme strings.
 *
 * @param {Boolean} [forwarding] - Page is being forwarded to results URL.
 * @param {Boolean} isActive - Tab is active.
 * @param {String} namespace - Unique namespace for form elements.
 * @param {Object} overlay - Overlay API.
 * @param {Boolean|Boolean} results - Product results for found vehicle.
 * @param {String} template - Template that contains form, `modal` or `section`.
 *
 * @emits vehicleResponse - Emits pre-formatted vehicle object response.
 *
 */
import { mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import VehicleType from './vehicle-type'

import IconChevronRight from '~icons/directional-navigation/chevron/right.svg'
import IconLoading from '~icons/general/loading.svg'

import vehicleFinderQuery from '~graphql/vehicle-finder.gql'

import {
  convertDropdownsToVehicle,
  formatVehicleDetailValue,
  getDefaultVehicleType,
  isYearHandle,
} from '~/helpers/garage'

import { transformEdges } from '~/helpers/graphql'

export default {
  name: 'FormDropdowns',

  components: {
    Btn,
    IconChevronRight,
    IconLoading,
    VehicleType,
  },

  props: {
    forwarding: {
      type: Boolean,
      default: false,
    },
    isActive: {
      type: Boolean,
      default: false,
      required: true,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    overlay: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    results: {
      type: [Boolean, Number],
      default: false,
      required: true,
    },
    template: {
      type: String,
      default: 'modal',
      required: true,
    },
  },

  emits: {
    'existing-vehicle': (payload) => typeof payload === 'object',
    'vehicle-response': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      combinedVrmIdData: {},
      controller: false,
      debugging: {
        values: {},
        vrmIdObjects: {},
      },
      dropdowns: {},
      dropdownsContainerStyle: {},
      dropdownState: 'loading',
      error: {
        input: '',
        message: '',
      },
      formState: 'ready',
      nextDropdowns: ['type'],
      optionalTypes: [],
      response: {
        filters: [],
        products: [],
        type: 'filters',
      },
      selected: {},
      selectedYear: '',
      type: '',
      types: [],
      typeTooltipActive: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getVehicleById: 'garage/getVehicleById',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute dropdown titles.
     * @returns {Object}
     */
    dropdownTitles() {
      const titles = {}

      Object.keys(this.dropdowns).forEach((type) => {
        const vehicleDetail = this.$variable(`vehicleFinder.vehicleDetails.${type}`)
        titles[type] = vehicleDetail.title
      })

      return titles
    },

    /**
     * Compute filters for query.
     * @returns {Array}
     */
    filters() {
      const filters = []

      if (!this.type) {
        return []
      }

      /**
       * Add type filter.
       */
      if (this.types?.length) {
        this.types.forEach((type) => {
          const value = type.includes(':') ? type.split(':')[1] : type

          filters.push({
            productMetafield: {
              key: this.$variable('vehicleFinder.vehicleDetails.type.key'),
              namespace: this.$variable('vehicleFinder.vehicleDetails.type.namespace'),
              value,
            },
          })
        })
      }

      /**
       * Add dropdown filters.
       */
      Object.values(this.selected).forEach((productMetafield) => {
        if (
          !productMetafield ||
          productMetafield === this.$string('vehicle_finder.dropdowns.all_years')
        ) {
          return
        }

        filters.push({ productMetafield })
      })

      return filters
    },

    /**
     * Compute local namespace.
     * @returns {String}
     */
    localNamespace() {
      return `${this.namespace}-dropdowns`
    },

    /**
     * Compute submit show state icon.
     * @returns {Boolean}
     */
    showSubmitStateIcon() {
      return this.formState === 'loading' || this.forwarding
    },

    /**
     * Compute submit CTA state icon.
     * @returns {String}
     */
    submitStateIcon() {
      return this.forwarding ? 'icon-chevron-right' : 'icon-loading'
    },
  },

  watch: {

    /**
     * Watch filters to update query.
     * @param {Array} newFilters - New filters.
     * @param {Array} oldFilters - Old filters.
     */
    filters(newFilters, oldFilters) {
      if (this.getFilterString(newFilters) === this.getFilterString(oldFilters)) {
        return
      }

      this.queryDropdownFilters()
    },

    /**
     * Watch overlay isActive to clear form.
     * - Reset form after tab transition has finished.
     */
    async 'overlay.isActive'() {
      if (this.overlay.isActive) {
        return
      }

      await cnvs.Sleep(this.$timing('normal'))
      this.resetForm()
    },

    /**
     * Watch is active to clear form.
     * - Reset form when it becomes active again.
     */
    isActive() {
      if (!this.isActive) {
        return
      }

      this.resetForm()
    },

    /**
     * Watch is mobile to clear dropdowns height.
     * - Height is set when transitioning height when switching vehicle type.
     */
    isMobile() {
      this.dropdownsStyle = {}
    },

    /**
     * Watch results to reset form.
     */
    results() {
      if (this.results !== false) {
        return
      }

      this.resetForm()
    },

    /**
     * Watch type update to set dropdowns.
     */
    type() {
      this.setDropdowns(true)
    },

    /**
     * Watch types update to set dropdowns.
     */
    types() {
      this.setDropdowns()
    },

    /**
     * Watch selected to update selected year.
     */
    selected: {
      handler() {
        const selectedYear = this.selected[this.$variable('vehicleFinder.handles.searchableYear')]

        if (!selectedYear) {
          return
        }

        this.selectedYear = typeof selectedYear === 'object'
          ? selectedYear.value
          : selectedYear
      },
      deep: true,
    },

    /**
     * Watch selected year to emit year mismatch event.
     */
    selectedYear() {
      this.emitYearMismatchEvent()
    },
  },

  /**
   * Create dropdowns order based on theme setting.
   */
  created() {
    this.setDropdowns()
  },

  mounted() {
    this.type = getDefaultVehicleType('dropdowns')
  },

  methods: {

    /**
     * Create filter string.
     * @returns {String}
     */
    getFilterString(filters) {
      return filters
        .map(({ productMetafield }) => {
          return `${productMetafield.key}-${productMetafield.filter}-${productMetafield.value}`
        })
        .join('//')
    },

    /**
     * Set dropdowns based on type.
     * @param {Boolean} [animate] - Animate dropdowns height.
     */
    async setDropdowns(animate) {
      if (animate) {
        this.animateDropdownsHeight(true)
      }

      const localType = this.type || 'Car'
      const keys = this.$variable('vehicleFinder.dropdowns')[localType]
      const dropdowns = {}
      const selected = {}
      const optionalTypes = []

      keys.forEach((key) => {
        const formattedKey = key.split('[')[0]?.trim()
        dropdowns[formattedKey] = []
        selected[formattedKey] = ''

        if (!key.includes('[optional]')) {
          return
        }

        optionalTypes.push(formattedKey)
      })

      this.dropdowns = dropdowns
      this.selected = selected
      this.optionalTypes = optionalTypes

      if (!animate) {
        return
      }

      await this.$nextTick()
      this.animateDropdownsHeight()
    },

    /**
     * Animate dropdowns height transition when changing type.
     * - Only on mobile with no motion preference.
     * @param {Boolean} start - Start height transition.
     */
    animateDropdownsHeight(start) {
      if (
        !this.isMobile ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        return
      }

      const container = this.$refs.dropdownsContainer
      const dropdowns = this.$refs.dropdowns

      if (start) {
        this.dropdownsContainerStyle = {
          height: `${dropdowns.offsetHeight}px`,
          overflow: 'hidden',
        }

        return
      }

      this.dropdownsContainerStyle.height = `${dropdowns.offsetHeight}px`

      container.ontransitionend = (args) => {
        if (!args || !['height'].includes(args.propertyName)) {
          return
        }

        this.dropdownsContainerStyle = {}
      }
    },

    /**
     * Get if dropdown is in loading state.
     * @param {String} key - Dropdown key.
     * @returns {Boolean}
     */
    getDropdownLoading(key) {
      return (
        this.nextDropdowns.includes(key) &&
        this.dropdownState === 'loading'
      )
    },

    /**
     * Get if dropdown is disabled.
     * - If previous dropdown has no value then it's disabled.
     * - If current dropdown has a value then it's not disabled.
     * - If previous dropdown is optional then find first previous dropdown
     *   which isn't optional.
     * @param {Number} index - Dropdown index.
     * @param {String} key - Dropdown key.
     * @returns {Boolean}
     */
    getDropdownDisabled(index, key) {
      if (!index) {
        return !this.type || this.getDropdownLoading(key)
      }

      const currentValue = Object.values(this.selected)[index]

      let previousIndex = 1
      let previousKey = Object.keys(this.selected)[index - previousIndex]

      while (this.optionalTypes.includes(previousKey)) {
        previousIndex += 1
        previousKey = Object.keys(this.selected)[index - previousIndex]
      }

      if (!previousKey || currentValue) {
        return false
      }

      if (this.dropdownState !== 'ready') {
        return true
      }

      const enabled = this.selected[previousKey]

      return !enabled || this.getDropdownLoading(key)
    },

    /**
     * Query collection filters to determine dropdown options.
     */
    async queryDropdownFilters() {
      if (!this.isActive) {
        return
      }

      try {
        if (this.controller.abort) {
          this.controller.abort()
        }

        this.controller = new AbortController()

        /**
         * Create array of next dropdowns.
         * - Include all next dropdowns that are optional if the first next
         *   dropdown is optional too.
         */
        const unselectedDropdowns = Object.keys(this.selected).filter((key) => {
          return !this.selected[key]
        })

        const nextDropdowns = []
        let firstIsOptional = false

        for (const [index, key] of unselectedDropdowns.entries()) {
          if (!index && this.optionalTypes.includes(key)) {
            firstIsOptional = true
          }

          if (!index || (firstIsOptional && this.optionalTypes.includes(key))) {
            nextDropdowns.push(key)
            continue
          }

          break
        }

        this.nextDropdowns = nextDropdowns

        /**
         * Determine product query.
         */
        const isProductQuery = this.nextDropdowns[0] !== 'make'
        this.dropdownState = 'loading'

        const variables = {
          country: this.$variable('store.country'),
          handle: this.$variable('vehicleFinder.dropdownsCollections')[this.type],
          includeFilters: true,
          language: this.$variable('store.language'),
          metafields: [],
          productCount: 0,
          productFilters: this.filters,
        }

        /**
         * Update query to use product metafield data instead of filters.
         */
        if (isProductQuery) {
          variables.includeFilters = false
          const searchableYearsHandle = this.$variable('vehicleFinder.handles.searchableYear')
          const searchableYears = this.$variable('vehicleFinder.vehicleDetails')[searchableYearsHandle]

          variables.metafields = [
            { namespace: 'filter', key: 'vehicles' },
            { namespace: searchableYears.namespace, key: searchableYears.key },
          ]

          variables.productCount = 200
        }

        const response = await cnvs.Query({
          signal: this.controller.signal,
          query: vehicleFinderQuery,
          variables,
        }, false)

        this.response = {
          filters: response?.collection?.products?.filters?.length
            ? response?.collection?.products?.filters
            : [],
          products: response?.collection?.products?.edges?.length
            ? transformEdges(response?.collection?.products)
            : [],
          type: isProductQuery ? 'products' : 'filters',
        }

        this.updateDropdowns()
        this.dropdownState = 'ready'

      } catch (error) {
        if (error.name === 'AbortError') {
          return
        }

        cnvs.ReportError('Failed to load dropdowns', error)
      }
    },

    /**
     * Update dropdowns.
     * - If key has a selection then do not update dropdown.
     * - This allows users to change their previous choices.
     */
    updateDropdowns() {
      const responseFilters = this.getResponseFilters()
      this.combinedVrmIdData = this.getCombinedVrmIdData()
      let afterCurrentDropdown = false

      /**
       * Update each dropdown's values.
       */
      Object.keys(this.dropdowns).forEach((key) => {
        if (this.selected[key] || afterCurrentDropdown) {
          return
        }

        const vehicleDetail = this.$variable(`vehicleFinder.vehicleDetails.${key}`)

        const dropdownValues = this.response?.type === 'products'
          ? this.updateDropdownsUsingProducts(vehicleDetail, responseFilters, key)
          : this.updateDropdownsUsingFilters(vehicleDetail)

        const sortedDropdownValues = dropdownValues.sort((a, b) => {
          if (isYearHandle(key)) {
            return b.label.localeCompare(a.label)
          }

          return a.label.localeCompare(b.label)
        })

        this.dropdowns[key] = isYearHandle(key)
          ? this.addAllYearsOption(sortedDropdownValues)
          : sortedDropdownValues

        /**
         * Do not update dropdowns after all next dropdowns.
         */
        if (this.nextDropdowns[this.nextDropdowns.length - 1] === key) {
          afterCurrentDropdown = true
        }
      })

      this.updateFocus()
    },

    /**
     * Get response filters from response.
     * - Find product filters that have values for response data.
     * - Used in product query to filter VRM ID data to just the objects that
     *   match the current product filters.
     * @returns {Object}
     */
    getResponseFilters() {
      const responseFilters = {}

      if (this.response?.type === 'products') {
        const vehicleDetails = this.$variable('vehicleFinder.vehicleDetails')

        this.filters.forEach(({ productMetafield }) => {
          const filterVehicleDetail = Object.values(vehicleDetails).find(({ key }) => {
            return key === productMetafield.key
          })

          if (!filterVehicleDetail || !filterVehicleDetail?.response) {
            return
          }

          responseFilters[filterVehicleDetail.response] = productMetafield.value
        })
      }

      return responseFilters
    },

    /**
     * Get combined product VRM ID data.
     * @returns {Object}
     */
    getCombinedVrmIdData() {
      let combinedVrmIdData = {}

      if (this.response?.type === 'products') {
        this.response.products.forEach((product) => {
          const value = product.metafields[0]?.value

          if (!value) {
            return
          }

          const vrmIdData = JSON.parse(value)

          combinedVrmIdData = {
            ...combinedVrmIdData,
            ...vrmIdData,
          }
        })
      }

      return combinedVrmIdData
    },

    /**
     * Update dropdowns using filters.
     * @param {Object} vehicleDetail - Vehicle detail matching dropdown.
     * @returns {Array}
     */
    updateDropdownsUsingFilters(vehicleDetail) {
      const matchingFilter = this.response?.filters?.find((filter) => {
        return filter.id === vehicleDetail.filter
      })

      if (!matchingFilter) {
        return []
      }

      return matchingFilter.values
        .filter((value) => value.count)
        .map((value) => {
          return {
            count: value.count,
            label: value.label,
            productMetafield: {
              key: vehicleDetail.key,
              namespace: vehicleDetail.namespace,
              value: value.label,
            },
          }
        })
    },

    /**
     * Update dropdowns using products.
     * - Uses VRM ID data to populate dropdown based on objects that match the
     *   current product filters.
     * - Response filters provides object of response keys and values based on
     *   previous dropdown selections.
     * - These are checked against each VRM ID object to ensure it has matching
     *   values for each key in response filters.
     * - If year dropdown then use filter.searchable_years metafield.
     * @param {Object} vehicleDetail - Vehicle detail matching dropdown.
     * @param {Object} responseFilters - Response filters.
     * @param {String} key - Dropdown key.
     * @returns {Array}
     */
    updateDropdownsUsingProducts(vehicleDetail, responseFilters, key) {
      const responseFiltersEntries = Object.entries(responseFilters)
      const unique = []

      const vrmIdObjects = Object.values(this.combinedVrmIdData)
        .filter((vrmIdObject) => {
          return responseFiltersEntries.every(([responseKey, responseValue]) => {
            return vrmIdObject[responseKey]?.some((vrmIdObjectValue) => {
              return String(vrmIdObjectValue)?.trim() === String(responseValue)
            })
          })
        })

      this.debugging.vrmIdObjects[key] = vrmIdObjects

      const values = vrmIdObjects
        .map((vrmIdObject) => {
          if (!vrmIdObject[vehicleDetail.response]) {
            return false
          }

          return vrmIdObject[vehicleDetail.response]
        })
        .flat()
        .filter((value) => {
          if (!value || unique.includes(value)) {
            return false
          }

          unique.push(value)
          return true
        })

      this.debugging.values[key] = values

      /**
       * If year dropdown then use dedicated metafield.
       * - Store VRM ID object year range to emit event.
       */
      if (isYearHandle(vehicleDetail.handle)) {
        const uniqueYears = []

        return this.response.products
          .map((product) => {
            const value = product.metafields[1]?.value

            if (!value) {
              return false
            }

            return JSON.parse(value)
          })
          .filter(Boolean)
          .flat()
          .filter((value) => {
            if (!value || uniqueYears.includes(value)) {
              return false
            }

            uniqueYears.push(value)
            return true
          })
          .map((value) => {
            const formattedValue = String(value).trim()

            return {
              count: 1,
              label: formattedValue,
              productMetafield: {
                key: vehicleDetail.key,
                namespace: vehicleDetail.namespace,
                value: formattedValue,
              },
            }
          })
      }

      /**
       * Convert VMR ID object values into metafields for dropdowns.
       */
      return values.map((value) => {
        const formattedValue = String(value).trim()

        return {
          count: 1,
          label: formattedValue,
          productMetafield: {
            key: vehicleDetail.key,
            namespace: vehicleDetail.namespace,
            value: formattedValue,
          },
        }
      })
    },

    /**
     * Add 'all years' value in years dropdown.
     * - When selected it has no metafield so products from all years are
     *   returned.
     * @param {Array<Object>} values - Existing dropdown values.
     * @returns {Array<Object>}
     */
    addAllYearsOption(values) {
      const allYearsValue = {
        count: 1,
        label: this.$string('vehicle_finder.dropdowns.all_years'),
        productMetafield: this.$string('vehicle_finder.dropdowns.all_years'),
      }

      return [
        allYearsValue,
        ...values,
      ]
    },

    /**
     * Update focus trap as select disabled states updates.
     */
    async updateFocus() {
      if (this.template !== 'modal') {
        return
      }

      await this.$nextTick()
      this.overlay.update({ currentElement: document.activeElement })
    },

    /**
     * Reset form.
     */
    async resetForm() {
      this.typeTooltipActive = false

      /**
       * Toggle type to force dropdowns query if dropdowns form was initially
       * inactive on page load.
       */
      this.type = ''
      await this.$nextTick()
      this.type = getDefaultVehicleType('dropdowns')

      this.error = {
        input: '',
        message: '',
      }

      this.formState = 'ready'
      this.dropdownState = 'ready'
    },

    /**
     * Handle type update.
     * @param {String} type - Vehicle type.
     * @param {String} otherType - 'Other Vehicle' type.
     */
    handleTypeUpdate(type, otherType) {
      if (!type && !otherType) {
        return
      }

      if (type) {
        this.type = type
      }

      if (otherType) {
        this.type = 'Other'
        this.types = [otherType]

      } else {
        this.types = this.$variable(`vehicleFinder.types.${type}`).map((value) => {
          if (!value.includes(':')) {
            return value
          }

          return value.split(':')[1]
        })
      }

      this.updateFocus()
    },

    /**
     * Handle tooltip update.
     * @param {Object} state - Tooltip state.
     */
    handleTooltipUpdate(state) {
      this.typeTooltipActive = state
    },

    /**
     * Handle selected updates.
     * @param {String|Object} newValue - New selected value.
     * @param {String} currentKey - Current selected object key.
     */
    handleSelectedUpdate(newValue, currentKey) {
      if (!newValue && !this.optionalTypes.includes(currentKey)) {
        return
      }

      /**
       * Only clear after current dropdown.
       */
      let clearAfter = false

      Object.entries(this.selected).forEach(([key, value]) => {
        if (clearAfter) {
          this.selected[key] = ''
          return
        }

        if (currentKey === key) {
          clearAfter = true
          return
        }

        this.selected[key] = value
      })
    },

    /**
     * Handle form submit.
     */
    handleSubmit() {
      this.error = {
        input: '',
        message: '',
      }

      this.formState = 'loading'

      /**
       * Set response based on selection.
       */
      const response = {}

      Object.entries(this.selected).forEach(([key, selection]) => {
        if (!selection?.value && !selection) {
          return
        }

        response[key] = selection.value || selection
      })

      response.type = this.type

      /**
       * Convert response into vehicle object.
       */
      const vehicle = convertDropdownsToVehicle(response)

      /**
       * Check if vehicle already exists in garage.
       */
      if (this.getVehicleById(vehicle.id)) {
        this.$emit('existing-vehicle', vehicle)
        return
      }

      this.$emit('vehicle-response', vehicle)
    },

    /**
     * Emit year event.
     * - Event tracks if selected year does not match the VRM ID objects' year
     *   range.
     */
    emitYearMismatchEvent() {
      const searchableYear = this.$variable('vehicleFinder.handles.searchableYear')
      const year = this.selectedYear
      const yearRange = this.debugging.values[searchableYear]

      if (
        year === this.$string('vehicle_finder.dropdowns.all_years') ||
        yearRange.includes(year)
      ) {
        return
      }

      const engine = this.$variable('vehicleFinder.handles.engine')
      const make = this.$variable('vehicleFinder.handles.make')
      const model = this.$variable('vehicleFinder.handles.model')
      const subModel = this.$variable('vehicleFinder.handles.subModel')

      const parameters = {
        vehicle_search_engine: formatVehicleDetailValue(this.selected[engine]?.value, engine),
        vehicle_search_form: 'Make-Model',
        vehicle_search_make: formatVehicleDetailValue(this.selected[make]?.value, make),
        vehicle_search_model: formatVehicleDetailValue(this.selected[model]?.value, model),
        vehicle_search_sub_model: formatVehicleDetailValue(
          this.selected[subModel]?.value,
          subModel,
        ),
        vehicle_search_type: this.type,
        vehicle_search_year: formatVehicleDetailValue(year, searchableYear),
        vehicle_search_year_range: formatVehicleDetailValue(yearRange, searchableYear),
      }

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'vehicle_search_year_mismatch',
        ...parameters,
      })
    },
  },
}
</script>
